<style lang="scss">
$teal:      #5bb8b1;
$teal-dark: #2c7d76;
$teal-bg:   #e4f3f0;
$dark:      #1a1a2e;
$gray:      #6c7280;
$font:      'Segoe UI', system-ui, sans-serif;

.disc-wrap {
  background: $teal-bg !important;
  min-height: 100vh;
  font-family: $font;
}

.disc-page-container {
  padding-top: 24px;
  padding-bottom: 90px;

  @media (min-width: 768px)  { padding-top: 32px; }
  @media (min-width: 1024px) { padding-top: 40px; max-width: 860px; margin: 0 auto; }
}

.disc-heading {
  margin-bottom: 20px;

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: $teal-dark;
    margin: 0 0 6px;

    @media (min-width: 768px) { font-size: 24px; }
  }

  &__sub {
    font-size: 13px;
    color: $gray;
    margin: 0;
  }
}

.disc-card {
  background: #fff;
  border-radius: 14px;
  border: 1.5px solid rgba(91, 184, 177, 0.2);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  font-family: $font;

  &__body {
    padding: 20px;
    font-size: 14px;
    color: $dark;
    line-height: 1.7;

    @media (min-width: 768px) { padding: 28px; font-size: 15px; }
  }
}

.rl-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 16px;
}

.rl-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  padding: 0 18px;
  margin: 0;
  background: #fff;
  color: $teal-dark;
  border: 1.5px solid $teal;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: rgba(91, 184, 177, 0.1); }
}

.rl-btn-input input {
  display: none;
}

.rl-btn-input span {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.rl-uploaded {
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #f0fff4;
  color: #276749;
  font-size: 13px;

  i { font-size: 18px; }
}

.rl-error {
  margin-top: 12px;
  font-size: 13px;
  color: #c0392b;
}

/* ── QR modal ── */
.rl-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(13, 27, 62, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.rl-qr-box {
  background: #fff;
  border-radius: 16px;
  padding: 32px 28px 24px;
  width: 340px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(13, 27, 62, 0.2);
  font-family: $font;
}

.rl-qr-title { font-size: 16px; font-weight: 700; color: $dark; margin-bottom: 8px; }
.rl-qr-sub { font-size: 12.5px; color: $gray; line-height: 1.5; margin-bottom: 20px; }

.rl-qr-code-wrap {
  display: flex;
  justify-content: center;
  padding: 12px;
  margin-bottom: 16px;
}

.rl-qr-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 180px;
  margin-bottom: 16px;
}

.rl-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #c5d8f5;
  border-top-color: $teal;
  border-radius: 50%;
  animation: rl-spin 0.7s linear infinite;
}

@keyframes rl-spin { to { transform: rotate(360deg); } }

.rl-qr-status {
  font-size: 13px;
  color: $gray;
  margin-bottom: 20px;

  &__ok { color: #276749; }
}

.rl-qr-close {
  width: 100%;
  height: 38px;
  border-radius: 8px;
  border: 1.5px solid rgba(91, 184, 177, 0.35);
  background: #fff;
  color: $dark;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;

  &:hover { background: #f4f6fb; }
}
</style>

<template>
  <section class="disc-wrap content d-flex flex-column flex-column-fluid">
    <div class="d-flex flex-column-fluid">
      <div class="container-fluid disc-page-container">

        <div class="disc-heading">
          <h2 class="disc-heading__title">Upload Request Letter</h2>
          <p class="disc-heading__sub">Please upload the attorney's request letter before continuing.</p>
        </div>

        <div class="row">
          <div class="col-12">
            <div class="disc-card">
              <div class="disc-card__body">
                <p>Upload the request letter from this computer, or scan a QR code to upload it from your phone.</p>

                <div class="rl-actions">
                  <label class="rl-btn rl-btn-input">
                    <input type="file" accept="image/*,application/pdf" @change="onComputerFileChange" />
                    <span v-if="uploadingDirect"><i class="fa fa-spinner fa-spin"></i> Uploading…</span>
                    <span v-else><i class="fa fa-upload"></i> Upload from computer</span>
                  </label>

                  <button class="rl-btn" @click="openQrModal">
                    <i class="fa fa-qrcode"></i> Upload from phone
                  </button>
                </div>

                <div class="rl-uploaded" v-if="requestLetter">
                  <i class="fa fa-check-circle"></i>
                  <span>{{ requestLetter.original_filename || 'Request letter' }} uploaded successfully.</span>
                </div>

                <div class="rl-error" v-if="errorMsg">
                  <i class="fa fa-exclamation-circle"></i> {{ errorMsg }}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
      <v-foooter
        v-on:next="nextStep"
        v-on:back="back"
        :btnname="'Next'"
        :backname="'Back'"
      ></v-foooter>
    </div>

    <!-- QR upload modal -->
    <transition name="rl-modal-fade">
      <div class="rl-overlay" v-if="qrModal.show" @click.self="closeQrModal">
        <div class="rl-qr-box">
          <div class="rl-qr-title">Scan to upload from phone</div>
          <div class="rl-qr-sub">Scan this code with your phone's camera to open the upload page and attach the request letter.</div>

          <div class="rl-qr-code-wrap" v-if="qrModal.uploadUrl">
            <qr-code :text="qrModal.uploadUrl" :size="180" bg-color="#ffffff" error-level="M"></qr-code>
          </div>
          <div class="rl-qr-loading" v-else>
            <div class="rl-spinner"></div>
          </div>

          <div class="rl-qr-status">
            <template v-if="qrModal.uploaded">
              <i class="fa fa-check-circle rl-qr-status__ok"></i> Uploaded successfully
            </template>
            <template v-else-if="qrModal.uploadUrl">
              <i class="fa fa-clock-o"></i> Waiting for upload…
            </template>
          </div>

          <button class="rl-qr-close" @click="closeQrModal">Close</button>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import vFoooter from "./citizen/foooter.vue";
import VueQRCodeComponent from "vue-qrcode-component";
import { resizeImageFile } from "@/core/services/imageresize";
import {
  UPLOAD_REQUEST_LETTER_DIRECT,
  GENERATE_REQUEST_LETTER_UPLOAD_TOKEN,
  GET_REQUEST_LETTER_UPLOAD_STATUS,
} from "@/core/services/store/actions.type";
import { SET_REQUEST_LETTER_ID } from "@/core/services/store/mutations.type";

export default {
  components: {
    vFoooter,
    "qr-code": VueQRCodeComponent,
  },
  data() {
    return {
      uploadingDirect: false,
      requestLetter: null,
      errorMsg: "",
      qrModal: {
        show: false,
        token: "",
        uploadUrl: "",
        uploaded: false,
      },
      qrPollTimer: null,
    };
  },
  computed: {
    ...mapGetters([
      "getfServiceSlug",
      "getServiceType",
      "getBiometricDataCollectionDisclosure",
      "getVerifiedAttorneyEmail",
    ]),
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    onComputerFileChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.uploadingDirect = true;
      this.errorMsg = "";
      resizeImageFile(file).then(resized => {
        this.$store
          .dispatch(UPLOAD_REQUEST_LETTER_DIRECT, {
            file: resized,
            attorney_email: this.getVerifiedAttorneyEmail,
          })
          .then(res => {
            this.uploadingDirect = false;
            this.requestLetter = res.data;
            this.$store.commit(SET_REQUEST_LETTER_ID, res.data.id);
          })
          .catch(err => {
            this.uploadingDirect = false;
            this.errorMsg = (err && err.msg) || "Upload failed";
          });
      });
      e.target.value = "";
    },
    openQrModal() {
      this.qrModal = { show: true, token: "", uploadUrl: "", uploaded: false };
      this.$store
        .dispatch(GENERATE_REQUEST_LETTER_UPLOAD_TOKEN, this.getVerifiedAttorneyEmail)
        .then(res => {
          this.qrModal.token = res.data.token;
          this.qrModal.uploadUrl = res.data.upload_url;
          this.startPolling();
        });
    },
    closeQrModal() {
      this.qrModal.show = false;
      this.stopPolling();
    },
    startPolling() {
      this.stopPolling();
      this.qrPollTimer = setInterval(() => {
        this.$store.dispatch(GET_REQUEST_LETTER_UPLOAD_STATUS, this.qrModal.token).then(res => {
          if (res.data.is_used) {
            this.qrModal.uploaded = true;
            if (res.data.request_letter) {
              this.requestLetter = res.data.request_letter;
              this.$store.commit(SET_REQUEST_LETTER_ID, res.data.request_letter.id);
            }
            this.stopPolling();
          }
        });
      }, 3000);
    },
    stopPolling() {
      if (this.qrPollTimer) {
        clearInterval(this.qrPollTimer);
        this.qrPollTimer = null;
      }
    },
    back() {
      var client_slug = this.$route.params.client_slug;
      var id = this.$route.params.id;
      this.$router.push({ name: "client.appointment.attorney-email", params: { id, client_slug } });
    },
    nextStep() {
      if (!this.requestLetter) {
        this.errorMsg = "Please upload the request letter to continue.";
        return;
      }
      var client_slug = this.$route.params.client_slug;
      var id = this.$route.params.id;

      if (this.getBiometricDataCollectionDisclosure) {
        this.$router.push({ name: "client.appointment.biometric-disclosure", params: { id, client_slug } });
      } else if (this.getServiceType == "citizen") {
        this.$router.push({ name: "client.appointment.citizenship", params: { id, client_slug } });
      } else if (this.getServiceType == "DISCLOSURE") {
        this.$router.push({ name: "client.appointment.disclosure", params: { id, client_slug } });
      } else {
        this.$router.push({ name: "frontend.booknow", params: { id, client_slug } });
      }
    },
  },
};
</script>
